<script lang="ts" module>
  import type { Component } from 'svelte';
  export interface ChoiceOption<T> {
    value: T;
    label: string;
    description?: string;
    icon?: Component<any>;
    /** Small badge text in the corner, e.g. "Aanbevolen". */
    badge?: string;
    disabled?: boolean;
  }
</script>

<script lang="ts" generics="T extends string | number">
  // Radio tiles: pick one of a few options that need a description or icon.
  //   <ChoiceCards bind:value={format} options={[{ value: 'pdf', label: 'PDF', description: '…', icon: FileText }]} cols={3} ariaLabel="Formaat" />
  // layout "tile" puts the icon on top (2–4 columns), "row" puts it left (lists).
  // Custom content per option: the `option` snippet gets (option, selected).
  // Keyboard: arrow keys move and select, like native radios.
  import type { Snippet } from 'svelte';
  import { Check } from '@lucide/svelte';

  interface Props {
    value: T | null;
    options: ChoiceOption<T>[];
    cols?: 1 | 2 | 3 | 4;
    layout?: 'tile' | 'row';
    size?: 'sm' | 'md';
    ariaLabel?: string;
    disabled?: boolean;
    onchange?: (value: T) => void;
    option?: Snippet<[ChoiceOption<T>, boolean]>;
    class?: string;
  }
  let { value = $bindable(null), options, cols = 2, layout = 'tile', size = 'md', ariaLabel, disabled = false, onchange, option: custom, class: cls = '' }: Props = $props();

  let root: HTMLElement | null = $state(null);
  const focusIndex = $derived(Math.max(0, options.findIndex((o) => o.value === value)));

  function choose(o: ChoiceOption<T>) {
    if (disabled || o.disabled) return;
    value = o.value;
    onchange?.(o.value);
  }

  function keydown(event: KeyboardEvent, index: number) {
    const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    const enabled = options.map((o, i) => (o.disabled ? -1 : i)).filter((i) => i >= 0);
    if (!enabled.length) return;
    const pos = enabled.indexOf(index);
    let next = enabled[0];
    if (event.key === 'End') next = enabled[enabled.length - 1];
    else if (event.key === 'Home') next = enabled[0];
    else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = enabled[(pos + 1) % enabled.length];
    else next = enabled[(pos - 1 + enabled.length) % enabled.length];
    choose(options[next]);
    root?.querySelectorAll<HTMLButtonElement>('[role="radio"]')[next]?.focus();
  }
</script>

<div class="choices {layout} {size} cols-{cols} {cls}" role="radiogroup" aria-label={ariaLabel} aria-disabled={disabled || undefined} bind:this={root}>
  {#each options as o, i (o.value)}
    {@const selected = o.value === value}
    <button
      type="button"
      role="radio"
      class="choice"
      class:selected
      aria-checked={selected}
      tabindex={i === focusIndex ? 0 : -1}
      disabled={disabled || o.disabled}
      onclick={() => choose(o)}
      onkeydown={(e) => keydown(e, i)}
    >
      {#if custom}
        {@render custom(o, selected)}
      {:else}
        {#if o.icon}<span class="icon" aria-hidden="true"><o.icon /></span>{/if}
        <span class="text">
          <span class="label">{o.label}</span>
          {#if o.description}<span class="desc">{o.description}</span>{/if}
        </span>
        {#if o.badge}<span class="badge">{o.badge}</span>{/if}
      {/if}
      <span class="check" aria-hidden="true"><Check strokeWidth={3} /></span>
    </button>
  {/each}
</div>

<style>
  .choices { display: grid; gap: var(--space-2_5); min-width: 0; }
  .cols-1 { grid-template-columns: minmax(0, 1fr); }
  .cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .cols-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
  @media (max-width: 640px) {
    .cols-3, .cols-4 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .row.cols-2, .row.cols-3, .row.cols-4 { grid-template-columns: minmax(0, 1fr); }
  }
  @media (max-width: 380px) {
    .cols-2, .cols-3, .cols-4 { grid-template-columns: minmax(0, 1fr); }
  }

  .choice {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-2);
    min-width: 0;
    padding: var(--space-3);
    padding-right: var(--space-8);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    background: var(--surface);
    color: var(--text);
    text-align: left;
    transition: border-color var(--dur), box-shadow var(--dur), background var(--dur);
  }
  .row .choice { flex-direction: row; align-items: center; gap: var(--space-3); }
  .sm .choice { padding: var(--space-2_5); padding-right: var(--space-8); border-radius: var(--radius-sm); }
  .choice:hover:not(:disabled) { border-color: var(--border-strong); }
  .choice:focus-visible { outline: none; box-shadow: var(--ring); }
  .choice.selected { border-color: var(--brand); background: var(--brand-softer); box-shadow: var(--ring-selected); }
  .choice.selected:focus-visible { box-shadow: var(--ring-selected), var(--ring); }
  .choice:disabled { opacity: 0.5; cursor: not-allowed; }

  .icon {
    width: 34px;
    height: 34px;
    border-radius: var(--radius-sm);
    display: grid;
    place-items: center;
    flex-shrink: 0;
    background: var(--surface-3);
    color: var(--text-2);
    transition: background var(--dur), color var(--dur);
  }
  .icon :global(svg) { width: var(--icon-lg); height: var(--icon-lg); }
  .sm .icon { width: 28px; height: 28px; }
  .sm .icon :global(svg) { width: var(--icon-md); height: var(--icon-md); }
  .selected .icon { background: var(--brand-soft); color: var(--brand); }
  .text { display: flex; flex-direction: column; gap: var(--space-0_5); min-width: 0; }
  .label { font-size: var(--text-base); font-weight: var(--weight-semibold); }
  .sm .label { font-size: var(--text-sm); }
  .desc { font-size: var(--text-xs); color: var(--text-2); line-height: var(--leading-snug); }
  .badge {
    position: absolute;
    top: var(--space-2);
    right: var(--space-8);
    font-size: var(--text-2xs);
    font-weight: var(--weight-semibold);
    padding: var(--space-0_5) var(--space-1_5);
    border-radius: var(--radius-pill);
    background: var(--brand-soft);
    color: var(--brand);
  }
  .check {
    position: absolute;
    top: var(--space-2_5);
    right: var(--space-2_5);
    width: 18px;
    height: 18px;
    border-radius: var(--radius-full);
    display: grid;
    place-items: center;
    border: 1.5px solid var(--border-strong);
    color: transparent;
    transition: background var(--dur), border-color var(--dur), color var(--dur);
  }
  .check :global(svg) { width: var(--icon-xs); height: var(--icon-xs); }
  .row .check { top: 50%; transform: translateY(-50%); }
  .selected .check { background: var(--brand); border-color: var(--brand); color: var(--on-brand); }
</style>
